import isEqual from 'lodash/isEqual';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query';
import { auth } from '../firebase';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { ProductIdsQueryArgs, ProductSearchQueryArgs, ProductSearchResponse, ProductsQuery, ProductStatsResponse, ProductStatsQuery, ProductIdArgs } from './models';
import { RootState } from '../store';
import { calculateSearchResultStats, mapCategoryStats, transformProductSearchResult } from './models-transformers';

export interface ApiState {
  queries: Record<string, ProductsQuery>;
}

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/products',
    prepareHeaders: async (headers) => {
      // Add the Authorization header dynamically before each request
      const token = auth.currentUser ? await auth.currentUser.getIdToken(false) : null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  endpoints: (builder) => ({
    searchProducts: builder.query<ProductSearchResponse, ProductSearchQueryArgs>({
      query: (productSearchQuery: ProductSearchQueryArgs) => {
        const params = [];
        if (!!productSearchQuery.q) {
          params.push(`q=${productSearchQuery.q}`);
        }
        if (productSearchQuery.limit) {
          params.push(`limit=${productSearchQuery.limit}`);
        }
        if (productSearchQuery.shop_ids && productSearchQuery.shop_ids.length) {
          params.push(productSearchQuery.shop_ids.map(shop_id => `shop_ids=${shop_id}`).join("&"));
        }
        if (productSearchQuery.category_ids && productSearchQuery.category_ids.length) {
          params.push(productSearchQuery.category_ids.map(category_id => `category_ids=${category_id}`).join("&"));
        }
        if (params.length) {
          return ["/search", params.join("&")].join("?");
        }
        return `/search`;
      },
      transformResponse: (response: ProductSearchResponse): ProductSearchResponse => {
        // Add calculated fields to each ProductSearchResult
        const transformedResults = response.results.map(data => transformProductSearchResult(data));
        const stats = calculateSearchResultStats(response?.results || [])
        return {
          ...response,
          results: transformedResults,
          stats: stats,
        };
      },
    }),
    getProductsById: builder.query<ProductSearchResponse, ProductIdsQueryArgs>({
      query: (productIdsQuery: ProductIdsQueryArgs) => {
        const idsParams = productIdsQuery.product_ids.map(product_id => `product_ids=${product_id}`);
        const queryString = idsParams.join("&");
        return `/ids?${queryString}`;
      },
      transformResponse: (response: ProductSearchResponse): ProductSearchResponse => {
        // Add calculated fields to each ProductSearchResult
        const transformedResults = response.results.map(data => transformProductSearchResult(data));
        const stats = calculateSearchResultStats(response?.results || [])
        return {
          ...response,
          results: transformedResults,
          stats: stats,
        };
      },
    }),
    getSimilarProducts: builder.query<ProductSearchResponse, ProductIdArgs>({
      query: (similarProductsQuery: ProductIdArgs) => {
        return `/similar/${similarProductsQuery.product_id}`;
      },
      transformResponse: (response: ProductSearchResponse): ProductSearchResponse => {
        // Add calculated fields to each ProductSearchResult
        const transformedResults = response.results.map(data => transformProductSearchResult(data));
        const stats = calculateSearchResultStats(response?.results || [])
        return {
          ...response,
          results: transformedResults,
          stats,
        };
      },
    }),
    getProductStats: builder.query<ProductStatsResponse, void>({
      query: (_v: void) => '/stats',
      transformResponse: (response: ProductStatsResponse): ProductStatsResponse => {
        const categoryMap = mapCategoryStats(response.categoryStats);
        return {
          ...response,
          categoryMap
        };
      }
    }),
  }),
});

export const searchProducts = createAsyncThunk<
  void,
  ProductSearchQueryArgs,
  {
    rejectValue: Error;
  }
>(
  'productsApi/search',
  async (productSearchQuery: ProductSearchQueryArgs, thunkAPI) => {
    await thunkAPI.dispatch(productsApi.endpoints.searchProducts.initiate(productSearchQuery));
  }
);
export const getProductsById = createAsyncThunk<
  void,
  ProductIdsQueryArgs,
  {
    rejectValue: Error;
  }
>(
  'productsApi/ids',
  async (productIdsQuery: ProductIdsQueryArgs, thunkAPI) => {
    await thunkAPI.dispatch(productsApi.endpoints.getProductsById.initiate(productIdsQuery));
  }
);

export const getSimilarProducts = createAsyncThunk<
  void,
  ProductIdArgs,
  {
    rejectValue: Error;
  }
>(
  'productsApi/similar',
  async (similarProductsQuery: ProductIdArgs, thunkAPI) => {
    await thunkAPI.dispatch(productsApi.endpoints.getSimilarProducts.initiate(similarProductsQuery));
  }
);

export const getProductStats = createAsyncThunk<
  void,
  void,
  {
    rejectValue: Error;
  }
>(
  'productsApi/stats',
  async (_, thunkAPI) => {
    await thunkAPI.dispatch(productsApi.endpoints.getProductStats.initiate());
  }
);

export const selectSlice = (state: any): ApiState => state.productsApi;

// all product search queries
export const selectProductSearchQueries = (
  state: RootState,
): ProductsQuery[] | null => {
  const ret: ProductsQuery[] | undefined = Object.values(selectSlice(state).queries);
  return ret || [];
};

// a specific product search query
export const selectProductSearchQuery = (
  state: RootState,
  productSearchQueryArgs: ProductSearchQueryArgs | null
): ProductsQuery | null => {
  const ret: ProductsQuery | undefined = Object.values(selectSlice(state).queries).find(
    (item) =>
      item.endpointName === 'searchProducts' &&
      isEqual(item.originalArgs, productSearchQueryArgs));
  return ret || null;
};

export const selectProductIdsQuery = (
  state: RootState,
  productIdsQuery: ProductIdsQueryArgs | null
): ProductsQuery | null => {
  const productsQueries = Object.values(selectSlice(state).queries);
  const ret: ProductsQuery | undefined = productsQueries.find((item) => {
    const match = (item.endpointName === 'getProductsById' && isEqual(item.originalArgs, productIdsQuery));
    return match;
  });
  return ret || null;
};

export const selectSimilarProductsQuery = (
  state: RootState,
  similarProductIdArgs: ProductIdArgs | null
): ProductsQuery | null => {
  const productsQueries = Object.values(selectSlice(state).queries);
  const ret: ProductsQuery | undefined = productsQueries.find((item) => {
    const match = (item.endpointName === 'getSimilarProducts' && isEqual(item.originalArgs, similarProductIdArgs));
    return match;
  });
  return ret || null;
};

export const selectProductStatsQuery = (
  state: RootState,
): ProductStatsQuery | null => {
  const found = Object.values(selectSlice(state).queries).filter(
    (item) =>
      item.endpointName === 'getProductStats'
  ).map(query => query as ProductStatsQuery); // fixme one day, dirty, move to separate createApi
  return found.length ? found[0] : null;
};

// export const selectCategoryStats = (state: RootState, category: string): CategoryStats | null => 
//   selectProductStatsQuery(state)?.data?.categoryStats.find(
//     categoryStat => categoryStat.category === category
//   ) || null;

// export const selectCategoryStats = (state: RootState, category: string): CategoryStats | undefined => 
//   selectProductStatsQuery(state)?.data?.categoryStats.find(
//     categoryStat => categoryStat.category === category
//   );
